import { useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { rosterRefusal } from "../../api/rosterRefusal";
import {
  checkBatch, getSponsorSettings, listMembers, reinstateMember, revokeMember, rowErrorsOf, saveSponsorSettings, uploadBatch,
  type AccountsResult, type MemberStatus, type RevokeAccess, type RosterMember, type RosterOrganisation, type RosterUpload,
  type SponsorSettings,
} from "../../api/roster";
import { indiaToday, planSummary, readRoster, sha256Hex, type RowProblem } from "../../domain/rosterCsv";
import { isSponsorOnly } from "../../components/nav";

/**
 * Member roster: an organisation (a university, a company) uploads the people
 * whose service sign-up it sponsors, and revokes them.
 *
 * 🔑 NOTHING HERE DECIDES. The browser reads the file and shows problems early;
 * radcustomer validates again, knows the approved plans and the organisation's
 * speed and validity limits, and saves the whole file or none of it. "Check with server" is its dry run,
 * and Upload stays disabled until that check passed for this exact file.
 *
 * Design: radius-wiki wiki/sponsor-roster-console-design.md §8 and §11.
 */

const SAMPLE =
  "mobile,full_name,category,member_ref,group_label,location_label,plan_id,speed_mbps,valid_days,valid_until\n" +
  "9000000001,Ravi Kumar Sharma,student,BT2024117,B.Tech CSE,H3-214,L-10000-ROSTER-2027-03,,,\n" +
  "9000000002,Ananya Deshpande,student,BT2024118,B.Tech CSE,H3-215,,10,180,\n" +
  "9000000003,Mohammed Irfan Shaikh,staff,EMP0312,Hostel Office,Admin Block,,10,,2027-03-31\n" +
  "9000000004,Priya R Nair,student,MS2025011,M.Sc Physics,H2-101,,,,\n";

const MAX_SHOWN_PROBLEMS = 200;

export function RosterScreen() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const sponsor = isSponsorOnly(identity.roles);

  if (!ns) {
    return <Alert severity="error">No namespace could be resolved from your sign-in, so there is no roster to show.</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 1100 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Member roster</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        People on this list get your organisation&apos;s plan when they sign up for service at <b>{ns}</b> with the same mobile
        number, or as soon as you upload them if they already have a subscriber account. Everyone else gets the hotspot&apos;s usual plan.
      </Typography>
      {sponsor ? (
        <>
          <UploadCard ns={ns} />
          <MembersCard ns={ns} />
          <SummaryCard ns={ns} />
        </>
      ) : (
        <Alert severity="info">
          <AlertTitle>For sponsor logins</AlertTitle>
          This screen works for an organisation&apos;s sponsor login, which is linked to its organisation. As the provider,
          open <Link component={RouterLink} to="/roster/organisations">Organisations</Link> to see each organisation&apos;s
          members, approve its plans and invite its sponsor.
        </Alert>
      )}
    </Box>
  );
}

function ProblemList({ title, problems }: { title: string; problems: RowProblem[] }) {
  if (problems.length === 0) return null;
  return (
    <Alert severity="error" sx={{ mt: 2 }}>
      <AlertTitle>{title}</AlertTitle>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {problems.slice(0, MAX_SHOWN_PROBLEMS).map((p, i) => (
          <li key={i}>
            {p.line > 1 ? `Line ${p.line}` : p.line === 1 ? "Header" : "File"} · {p.field}: {p.message}
          </li>
        ))}
      </Box>
      {problems.length > MAX_SHOWN_PROBLEMS && (
        <Typography variant="body2" sx={{ mt: 1 }}>…and {problems.length - MAX_SHOWN_PROBLEMS} more.</Typography>
      )}
    </Alert>
  );
}

function UploadCard({ ns }: { ns: string }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [upload, setUpload] = useState<RosterUpload | null>(null);
  const [problems, setProblems] = useState<RowProblem[]>([]);
  const [checkedSha, setCheckedSha] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ rows: number; filename: string; plans: string[]; accounts?: AccountsResult | undefined } | null>(null);

  const reset = () => {
    setUpload(null);
    setProblems([]);
    setCheckedSha(null);
    check.reset();
    save.reset();
  };

  const check = useMutation({
    mutationFn: async (u: RosterUpload) => (await checkBatch(identity, ns, u)).data,
    onSuccess: (_, u) => setCheckedSha(u.file_sha256),
  });
  const save = useMutation({
    mutationFn: async (u: RosterUpload) => (await uploadBatch(identity, ns, u)).data,
    onSuccess: (data, u) => {
      setSaved({ rows: data.rows, filename: u.filename, plans: data.plans_created ?? [], accounts: data.accounts });
      setUpload(null);
      setCheckedSha(null);
      if (input.current) input.current.value = "";
      void qc.invalidateQueries({ queryKey: ["rosterMembers", ns] });
    },
  });

  const onFile = async (file: File | undefined) => {
    reset();
    setSaved(null);
    if (!file) return;
    const bytes = new Uint8Array(await file.arrayBuffer());
    let text: string;
    try {
      text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    } catch {
      setProblems([{ line: 0, field: "file", message: "the file is not UTF-8 text. Save it from your spreadsheet as CSV UTF-8." }]);
      return;
    }
    const read = readRoster(text, indiaToday(new Date()));
    setProblems(read.problems);
    if (read.problems.length === 0) {
      setUpload({ filename: file.name, file_sha256: await sha256Hex(bytes), rows: read.rows });
    }
  };

  const serverRows = [...rowErrorsOf(check.error), ...rowErrorsOf(save.error)];
  const serverMessage = check.error ?? save.error;
  const checked = upload !== null && checkedSha === upload.file_sha256;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Upload a list</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        A CSV file with the columns <code>mobile, full_name, category, member_ref</code>, and optionally{" "}
        <code>group_label, location_label</code>. Each row's plan: an approved <code>plan_id</code> on its own; or{" "}
        <code>speed_mbps</code> with <code>valid_days</code> or <code>valid_until</code> (not both); or nothing, for 5 Mbps
        for 30 days. Days count from the member's first sign-in; dates are YYYY-MM-DD. Uploading a mobile already on
        your list updates it. Up to 5000 rows at a time; if any row has a problem, nothing is saved.{" "}
        <Link href={`data:text/csv;charset=utf-8,${encodeURIComponent(SAMPLE)}`} download="roster-sample.csv">
          Download a sample file
        </Link>
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { sm: "center" } }}>
        <Button variant="outlined" component="label">
          Choose file
          <input
            ref={input}
            hidden
            type="file"
            accept=".csv,text/csv"
            data-testid="roster-file"
            onChange={(e) => void onFile(e.target.files?.[0])}
          />
        </Button>
        {upload && (
          <Typography variant="body2">
            <b>{upload.filename}</b> · {upload.rows.length} row{upload.rows.length === 1 ? "" : "s"}
          </Typography>
        )}
      </Stack>

      <ProblemList title="Fix these lines in the file, then choose it again" problems={problems} />

      {upload && (
        <>
          <PreviewTable upload={upload} />
          <Stack direction="row" spacing={1.5} sx={{ mt: 2, flexWrap: "wrap" }} useFlexGap>
            <Button variant="outlined" disabled={check.isPending || save.isPending} onClick={() => check.mutate(upload)}>
              {check.isPending ? "Checking…" : "Check with server"}
            </Button>
            <Button
              variant="contained"
              disabled={!checked || save.isPending}
              onClick={() => save.mutate(upload)}
            >
              {save.isPending ? "Uploading…" : `Upload ${upload.rows.length} member${upload.rows.length === 1 ? "" : "s"}`}
            </Button>
            <Button onClick={() => { reset(); if (input.current) input.current.value = ""; }}>Clear</Button>
          </Stack>
          {!checked && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Upload unlocks after &ldquo;Check with server&rdquo; finds no problems in this file. Nothing is saved by the check.
            </Typography>
          )}
          {checked && !save.error && (
            <Alert severity="success" sx={{ mt: 2 }}>
              The server checked all {upload.rows.length} rows and found no problems. Nothing is saved until you upload.
              {(check.data?.plans_to_create?.length ?? 0) > 0 &&
                ` Uploading also creates the shared plan${check.data!.plans_to_create!.length === 1 ? "" : "s"} ${check.data!.plans_to_create!.join(", ")}.`}
            </Alert>
          )}
        </>
      )}

      {serverMessage && (
        serverRows.length > 0 ? (
          <ProblemList title={serverMessage.message} problems={serverRows} />
        ) : (
          <Alert severity="error" sx={{ mt: 2 }}>{rosterRefusal(serverMessage, ns)}</Alert>
        )
      )}
      {saved && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Saved {saved.rows} member{saved.rows === 1 ? "" : "s"} from <b>{saved.filename}</b>.
          {saved.plans.length > 0 && ` Created the shared plan${saved.plans.length === 1 ? "" : "s"} ${saved.plans.join(", ")}.`}
        </Alert>
      )}
      {saved && <AccountsNote accounts={saved.accounts} />}
    </Paper>
  );
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** What the upload did to members who already have a subscriber account. The rows are saved whatever this says. */
function AccountsNote({ accounts }: { accounts: AccountsResult | undefined }) {
  if (!accounts) return null;
  const failed = accounts.failed ?? [];
  return (
    <>
      {accounts.applied > 0 && (
        <Alert severity="success" sx={{ mt: 1 }} data-testid="roster-accounts-applied">
          {accounts.applied} {plural(accounts.applied, "member who already had a subscriber account now has", "members who already had a subscriber account now have")}{" "}
          your organisation&apos;s plan.
        </Alert>
      )}
      {failed.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }} data-testid="roster-accounts-failed">
          <AlertTitle>
            {failed.length} {plural(failed.length, "member", "members")} could not be given the plan yet
          </AlertTitle>
          Their rows are saved. Upload the same file again later to retry.
          <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
            {failed.slice(0, MAX_SHOWN_PROBLEMS).map((f, i) => (
              <li key={i}>{f.line ? `Line ${f.line} · ` : ""}{f.mobile}</li>
            ))}
          </Box>
        </Alert>
      )}
      {accounts.waiting > 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {accounts.waiting} {plural(accounts.waiting, "member is", "members are")} still to be moved onto the plan. Upload the same file
          again to finish.
        </Alert>
      )}
      {accounts.unavailable && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          The rows are saved, but members who already have a subscriber account were not moved onto the plan: {accounts.unavailable}
        </Alert>
      )}
    </>
  );
}

function PreviewTable({ upload }: { upload: RosterUpload }) {
  const shown = upload.rows.slice(0, 20);
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Line</TableCell>
            <TableCell>Mobile</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Member ID</TableCell>
            <TableCell>Group</TableCell>
            <TableCell>Location</TableCell>
            <TableCell>Plan</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {shown.map((r) => (
            <TableRow key={r.line}>
              <TableCell>{r.line}</TableCell>
              <TableCell>{r.mobile}</TableCell>
              <TableCell>{r.full_name}</TableCell>
              <TableCell>{r.category}</TableCell>
              <TableCell>{r.member_ref}</TableCell>
              <TableCell>{r.group_label ?? ""}</TableCell>
              <TableCell>{r.location_label ?? ""}</TableCell>
              <TableCell>{planSummary(r)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {upload.rows.length > shown.length && (
        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
          Showing the first {shown.length} of {upload.rows.length} rows.
        </Typography>
      )}
    </TableContainer>
  );
}

/** A stored member's plan in words, as the upload preview says it. */
function memberPlan(m: RosterMember): string {
  if (m.plan_source === "category") return m.valid_until ? `category plan to ${m.valid_until}` : "category plan";
  if (m.plan_source === "plan_id") return m.groupname ?? "";
  const speed = m.speed_mbps ? `${m.speed_mbps} Mbps` : "";
  const validity = m.valid_until ? `to ${m.valid_until}` : m.valid_days ? `${m.valid_days} days` : "";
  return [speed, validity].filter(Boolean).join(m.valid_until ? " " : ", ");
}

const STATUS_COLOUR: Record<MemberStatus, "default" | "success" | "warning"> = {
  pending: "warning",
  activated: "success",
  revoked: "default",
};

/** What a revoke did to the member's service, in words (radcustomer's "access"). */
export function revokeNotice(name: string, access: RevokeAccess | undefined): string {
  switch (access) {
    case "restored":
      return `${name} is off your list and back on the plan they had before.`;
    case "blocked":
      return `${name} is off your list. Their next sign-in will be refused; a device already online stays connected until it disconnects or sits idle for 10 minutes.`;
    case "unchanged":
      return `${name} is off your list. Their subscriber account is on a different plan now, so it was left as it is.`;
    default:
      return `${name} is off your list and will not get your organisation's plan.`;
  }
}

function MembersCard({ ns }: { ns: string }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [status, setStatus] = useState<MemberStatus | "">("");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [cursors, setCursors] = useState<number[]>([0]);
  const [confirm, setConfirm] = useState<RosterMember | null>(null);
  const [reinstating, setReinstating] = useState<RosterMember | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const cursor = cursors[cursors.length - 1] ?? 0;

  const list = useQuery({
    queryKey: ["rosterMembers", ns, status, q, cursor],
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: async () => (await listMembers(identity, ns, { status: status || undefined, q, cursor })).data,
  });

  const revoke = useMutation({
    mutationFn: async (m: RosterMember) => (await revokeMember(identity, ns, m.id)).data,
    onSuccess: (data, m) => {
      setConfirm(null);
      setNotice(revokeNotice(m.full_name, data.access));
      void qc.invalidateQueries({ queryKey: ["rosterMembers", ns] });
    },
  });

  const reinstate = useMutation({
    mutationFn: async (m: RosterMember) => (await reinstateMember(identity, ns, m.id)).data,
    onSuccess: (data, m) => {
      setReinstating(null);
      setNotice(
        data.status === "activated"
          ? `${m.full_name} is back on your list and on your organisation's plan.`
          : `${m.full_name} is back on your list and gets your organisation's plan when they sign up.`,
      );
      void qc.invalidateQueries({ queryKey: ["rosterMembers", ns] });
    },
  });

  const org: RosterOrganisation | undefined = list.data?.organisation;
  const members = list.data?.members ?? [];
  const firstPage = () => setCursors([0]);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
          Members{org ? ` of ${org.name}` : ""}
        </Typography>
        {org && org.status !== "active" && <Chip size="small" color="error" label={org.status} />}
      </Stack>
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)} data-testid="roster-notice">
          {notice}
        </Alert>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => { setStatus(e.target.value as MemberStatus | ""); firstPage(); }}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="pending">Not signed up yet</MenuItem>
          <MenuItem value="activated">Signed up</MenuItem>
          <MenuItem value="revoked">Revoked</MenuItem>
        </TextField>
        <TextField
          size="small"
          label="Search mobile, name or ID"
          value={search}
          slotProps={{ htmlInput: { maxLength: 64 } }}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { setQ(search); firstPage(); } }}
          sx={{ flex: 1 }}
        />
        <Button variant="outlined" onClick={() => { setQ(search); firstPage(); }}>Search</Button>
      </Stack>

      {list.isLoading && <Typography variant="body2">Loading…</Typography>}
      {list.isError && <Alert severity="error">{rosterRefusal(list.error, ns)}</Alert>}
      {list.data && members.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {q || status ? "No members match." : "No members yet. Upload a list above."}
        </Typography>
      )}
      {members.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Mobile</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>{org?.member_ref_label || "Member ID"}</TableCell>
                <TableCell>{org?.group_label_label || "Group"}</TableCell>
                <TableCell>{org?.location_label_label || "Location"}</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((m) => (
                <TableRow key={m.id} hover>
                  <TableCell>{m.mobile}</TableCell>
                  <TableCell>{m.full_name}</TableCell>
                  <TableCell>{m.category}</TableCell>
                  <TableCell>{m.member_ref}</TableCell>
                  <TableCell>{m.group_label ?? ""}</TableCell>
                  <TableCell>{m.location_label ?? ""}</TableCell>
                  <TableCell>
                    {memberPlan(m)}
                    {m.apply_error && m.status !== "revoked" && (
                      <Chip size="small" color="warning" label="not given yet" title={m.apply_error} sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell><Chip size="small" color={STATUS_COLOUR[m.status]} label={m.status} /></TableCell>
                  <TableCell align="right">
                    {m.status !== "revoked" ? (
                      <Button size="small" color="error" onClick={() => { revoke.reset(); setConfirm(m); }}>Revoke</Button>
                    ) : (
                      <Button size="small" onClick={() => { reinstate.reset(); setReinstating(m); }}>Reinstate</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {(cursors.length > 1 || (list.data?.next_cursor ?? 0) > 0) && (
        <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
          <Button disabled={cursors.length <= 1} onClick={() => setCursors(cursors.slice(0, -1))}>Previous</Button>
          <Button
            disabled={!list.data?.next_cursor}
            onClick={() => list.data?.next_cursor && setCursors([...cursors, list.data.next_cursor])}
          >
            Next
          </Button>
        </Stack>
      )}

      <Dialog open={confirm !== null} onClose={() => setConfirm(null)}>
        <DialogTitle>Revoke {confirm?.full_name}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirm?.mobile} will no longer get your organisation&apos;s plan.
            {confirm?.status === "activated" &&
              " They have already signed up: they go back to the plan they had before, or, if they had none, their next sign-in is refused. A device already online stays connected until it disconnects."}
          </Typography>
          {revoke.isError && <Alert severity="error" sx={{ mt: 2 }}>{rosterRefusal(revoke.error, ns)}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={revoke.isPending} onClick={() => confirm && revoke.mutate(confirm)}>
            {revoke.isPending ? "Revoking…" : "Revoke"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reinstating !== null} onClose={() => setReinstating(null)}>
        <DialogTitle>Reinstate {reinstating?.full_name}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {reinstating?.mobile} gets your organisation&apos;s plan again. If they have a subscriber account, their next sign-in works on
            that plan; otherwise they get it when they sign up.
          </Typography>
          {reinstate.isError && <Alert severity="error" sx={{ mt: 2 }}>{rosterRefusal(reinstate.error, ns)}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReinstating(null)}>Cancel</Button>
          <Button variant="contained" disabled={reinstate.isPending} onClick={() => reinstating && reinstate.mutate(reinstating)}>
            {reinstate.isPending ? "Reinstating…" : "Reinstate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

/**
 * The weekly summary: where it goes, and whether it is sent at all (design
 * §13.8).
 *
 * 🔑 THE SUMMARY IS COUNTS ONLY -- how many were added, signed up, taken off,
 * and how many end within seven days. It carries no name and no mobile
 * number, because the address is not verified and mail goes astray. Whoever
 * has to act on the list signs in to see it. Saying so here is the point: a
 * sponsor deciding whether to type an address deserves to know what the
 * address will receive.
 *
 * Only the signed-in sponsor can change this. The gateway refuses the route to
 * an operator, so there is no operator view of it to build.
 */
function SummaryCard({ ns }: { ns: string }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [email, setEmail] = useState<string | null>(null);
  const [digest, setDigest] = useState<boolean | null>(null);
  const [saved, setSaved] = useState(false);

  const settings = useQuery({
    queryKey: ["roster", ns, "sponsor-settings"],
    queryFn: () => getSponsorSettings(identity, ns).then((r) => r.data),
  });

  const save = useMutation({
    mutationFn: (s: SponsorSettings) => saveSponsorSettings(identity, ns, s),
    onSuccess: async () => {
      setSaved(true);
      setEmail(null);
      setDigest(null);
      await qc.invalidateQueries({ queryKey: ["roster", ns, "sponsor-settings"] });
    },
  });

  if (settings.isError) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Weekly summary</Typography>
        <Alert severity="error">{rosterRefusal(settings.error, ns)}</Alert>
      </Paper>
    );
  }

  const current = settings.data;
  // The typed value until it is saved, then the server's own answer again.
  const shownEmail = email ?? current?.email ?? "";
  const shownDigest = digest ?? current?.digest ?? false;
  const changed = current ? shownEmail.trim() !== current.email || shownDigest !== current.digest : false;
  // An address is not checked here beyond having an @: radcustomer decides, and
  // a screen that guesses at addresses refuses ones that work.
  const badEmail = shownEmail.trim() !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(shownEmail.trim());
  const wantsSummaryWithNoAddress = shownDigest && shownEmail.trim() === "";

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Weekly summary</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every Monday morning we can email you how the list moved in the past week: how many people you added, how many
        signed up, how many you took off, and how many are due to end within seven days. <b>Numbers only</b> — never a
        name or a mobile number, because email goes astray. A week with no movement is not sent.
      </Typography>
      <Stack spacing={2} sx={{ maxWidth: 520 }}>
        <TextField
          label="Summary address"
          value={shownEmail}
          onChange={(e) => { setEmail(e.target.value); setSaved(false); }}
          disabled={settings.isLoading || save.isPending}
          error={badEmail}
          helperText={
            badEmail
              ? "That is not an address. Leave it empty to stop the summary."
              : "Leave it empty to stop the summary reaching an address you no longer use."
          }
          size="small"
          fullWidth
        />
        <FormControlLabel
          control={
            <Switch
              checked={shownDigest}
              onChange={(e) => { setDigest(e.target.checked); setSaved(false); }}
              disabled={settings.isLoading || save.isPending}
            />
          }
          label="Send me the weekly summary"
        />
        {wantsSummaryWithNoAddress && (
          <Alert severity="info">Nothing will be sent until there is an address to send it to.</Alert>
        )}
        {save.isError && <Alert severity="error">{rosterRefusal(save.error, ns)}</Alert>}
        {saved && !changed && <Alert severity="success">Saved.</Alert>}
        <Box>
          <Button
            variant="contained"
            disabled={!changed || badEmail || save.isPending || settings.isLoading}
            onClick={() => save.mutate({ email: shownEmail.trim(), digest: shownDigest })}
          >
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}
